import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, CheckCircle, XCircle, Clock, Trash2, FileDown } from 'lucide-react';
import { DOWNLOAD_STATUS } from '../hooks/useDownload';

const DownloadProgress = ({ 
  tasks, 
  onDownloadFile, 
  onRemoveTask, 
  formatFileSize, 
  formatDuration 
}) => {
  const { t, i18n } = useTranslation();

  if (!tasks || tasks.length === 0) {
    return (
      <div className="text-center py-12">
        <Download className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 text-lg">{t('info')}</p>
        <p className="text-gray-400 text-sm mt-2">{t('downloadVideo')}</p>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case DOWNLOAD_STATUS.COMPLETED:
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case DOWNLOAD_STATUS.FAILED:
        return <XCircle className="w-5 h-5 text-red-500" />;
      case DOWNLOAD_STATUS.DOWNLOADING:
        return <Download className="w-5 h-5 text-blue-500 animate-pulse" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case DOWNLOAD_STATUS.PENDING:
        return i18n.language === 'zh' ? '等待中' :
               i18n.language === 'en' ? 'Pending' :
               i18n.language === 'ja' ? '待機中' :
               i18n.language === 'ko' ? '대기 중' :
               i18n.language === 'es' ? 'Pendiente' :
               i18n.language === 'fr' ? 'En attente' :
               i18n.language === 'de' ? 'Wartend' :
               i18n.language === 'ru' ? 'Ожидание' :
               'Pending';
      case DOWNLOAD_STATUS.DOWNLOADING:
        return t('downloading');
      case DOWNLOAD_STATUS.COMPLETED:
        return i18n.language === 'zh' ? '已完成' :
               i18n.language === 'en' ? 'Completed' :
               i18n.language === 'ja' ? '完了' :
               i18n.language === 'ko' ? '완료' :
               i18n.language === 'es' ? 'Completado' :
               i18n.language === 'fr' ? 'Terminé' :
               i18n.language === 'de' ? 'Abgeschlossen' :
               i18n.language === 'ru' ? 'Завершено' :
               'Completed';
      case DOWNLOAD_STATUS.FAILED:
        return i18n.language === 'zh' ? '失败' :
               i18n.language === 'en' ? 'Failed' :
               i18n.language === 'ja' ? '失敗' :
               i18n.language === 'ko' ? '실패' :
               i18n.language === 'es' ? 'Fallido' :
               i18n.language === 'fr' ? 'Échoué' :
               i18n.language === 'de' ? 'Fehlgeschlagen' :
               i18n.language === 'ru' ? 'Неудачно' :
               'Failed';
      case DOWNLOAD_STATUS.CANCELLED:
        return i18n.language === 'zh' ? '已取消' :
               i18n.language === 'en' ? 'Cancelled' :
               i18n.language === 'ja' ? 'キャンセル済み' :
               i18n.language === 'ko' ? '취소됨' :
               i18n.language === 'es' ? 'Cancelado' :
               i18n.language === 'fr' ? 'Annulé' :
               i18n.language === 'de' ? 'Abgebrochen' :
               i18n.language === 'ru' ? 'Отменено' :
               'Cancelled';
      default:
        return i18n.language === 'zh' ? '未知' :
               i18n.language === 'en' ? 'Unknown' :
               i18n.language === 'ja' ? '不明' :
               i18n.language === 'ko' ? '알 수 없음' :
               i18n.language === 'es' ? 'Desconocido' :
               i18n.language === 'fr' ? 'Inconnu' :
               i18n.language === 'de' ? 'Unbekannt' :
               i18n.language === 'ru' ? 'Неизвестно' :
               'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case DOWNLOAD_STATUS.COMPLETED:
        return 'text-green-600 bg-green-50';
      case DOWNLOAD_STATUS.FAILED:
        return 'text-red-600 bg-red-50';
      case DOWNLOAD_STATUS.DOWNLOADING:
        return 'text-blue-600 bg-blue-50';
      case DOWNLOAD_STATUS.PENDING:
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatCreatedAt = (date) => {
    if (!date) return '';
    
    // 格式化日期时间，显示本地时区的完整日期和时间
    try {
      // 获取当前语言对应的日期格式选项
      const dateOptions = { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit',
        hour12: false
      };
      
      // 根据当前语言格式化日期
      let locale = 'en-US';
      if (i18n.language === 'zh') locale = 'zh-CN';
      else if (i18n.language === 'ja') locale = 'ja-JP';
      else if (i18n.language === 'ko') locale = 'ko-KR';
      else if (i18n.language === 'es') locale = 'es-ES';
      else if (i18n.language === 'fr') locale = 'fr-FR';
      else if (i18n.language === 'de') locale = 'de-DE';
      else if (i18n.language === 'ru') locale = 'ru-RU';
      
      return date.toLocaleString(locale, dateOptions);
    } catch (err) {
      // 回退到简单的日期格式
      return date.toLocaleString();
    }
  };

  const getLocalizedText = (key) => {
    const texts = {
      downloadTasks: {
        zh: '下载任务',
        en: 'Download Tasks',
        ja: 'ダウンロードタスク',
        ko: '다운로드 작업',
        es: 'Tareas de Descarga',
        fr: 'Tâches de Téléchargement',
        de: 'Download-Aufgaben',
        ru: 'Задачи Загрузки'
      },
      totalTasks: {
        zh: '共',
        en: 'Total',
        ja: '合計',
        ko: '총',
        es: 'Total',
        fr: 'Total',
        de: 'Gesamt',
        ru: 'Всего'
      },
      tasks: {
        zh: '个任务',
        en: 'tasks',
        ja: 'タスク',
        ko: '개 작업',
        es: 'tareas',
        fr: 'tâches',
        de: 'Aufgaben',
        ru: 'задач'
      },
      unknownFile: {
        zh: '未知文件',
        en: 'Unknown File',
        ja: '不明なファイル',
        ko: '알 수 없는 파일',
        es: 'Archivo Desconocido',
        fr: 'Fichier Inconnu',
        de: 'Unbekannte Datei',
        ru: 'Неизвестный Файл'
      },
      deleteTask: {
        zh: '删除任务',
        en: 'Delete Task',
        ja: 'タスクを削除',
        ko: '작업 삭제',
        es: 'Eliminar Tarea',
        fr: 'Supprimer la Tâche',
        de: 'Aufgabe Löschen',
        ru: 'Удалить Задачу'
      },
      downloadProgress: {
        zh: '下载进度',
        en: 'Download Progress',
        ja: 'ダウンロード進度',
        ko: '다운로드 진행률',
        es: 'Progreso de Descarga',
        fr: 'Progression du Téléchargement',
        de: 'Download-Fortschritt',
        ru: 'Прогресс Загрузки'
      },
      format: {
        zh: '格式:',
        en: 'Format:',
        ja: 'フォーマット:',
        ko: '형식:',
        es: 'Formato:',
        fr: 'Format:',
        de: 'Format:',
        ru: 'Формат:'
      },
      fileType: {
        zh: '文件类型:',
        en: 'File Type:',
        ja: 'ファイルタイプ:',
        ko: '파일 유형:',
        es: 'Tipo de Archivo:',
        fr: 'Type de Fichier:',
        de: 'Dateityp:',
        ru: 'Тип Файла:'
      },
      size: {
        zh: '大小:',
        en: 'Size:',
        ja: 'サイズ:',
        ko: '크기:',
        es: 'Tamaño:',
        fr: 'Taille:',
        de: 'Größe:',
        ru: 'Размер:'
      },
      createdTime: {
        zh: '创建时间:',
        en: 'Created:',
        ja: '作成時間:',
        ko: '생성 시간:',
        es: 'Creado:',
        fr: 'Créé:',
        de: 'Erstellt:',
        ru: 'Создано:'
      },
      downloadFile: {
        zh: '下载文件',
        en: 'Download File',
        ja: 'ファイルをダウンロード',
        ko: '파일 다운로드',
        es: 'Descargar Archivo',
        fr: 'Télécharger le Fichier',
        de: 'Datei Herunterladen',
        ru: 'Скачать Файл'
      },
      error: {
        zh: '错误:',
        en: 'Error:',
        ja: 'エラー:',
        ko: '오류:',
        es: 'Error:',
        fr: 'Erreur:',
        de: 'Fehler:',
        ru: 'Ошибка:'
      }
    };
    
    return texts[key]?.[i18n.language] || texts[key]?.en || key;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{getLocalizedText('downloadTasks')}</h2>
        <div className="text-sm text-gray-500">
          {getLocalizedText('totalTasks')} {tasks.length} {getLocalizedText('tasks')}
        </div>
      </div>

      {tasks.map((task) => (
        <div
          key={task.id}
          className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
        >
          <div className="p-6">
            {/* 任务头部信息 */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-medium text-gray-900 truncate">
                  {task.filename || getLocalizedText('unknownFile')}
                </h3>
                <p className="text-sm text-gray-500 truncate mt-1">
                  {task.url}
                </p>
              </div>
              
              <div className="flex items-center gap-2 ml-4">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                  {getStatusIcon(task.status)}
                  {getStatusText(task.status)}
                </span>
                
                <button
                  onClick={() => onRemoveTask(task.id)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  title={getLocalizedText('deleteTask')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* 进度条 */}
            {(task.status === DOWNLOAD_STATUS.DOWNLOADING || task.progress > 0) && (
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>{getLocalizedText('downloadProgress')}</span>
                  <span>{Math.round(task.progress || 0)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${task.progress || 0}%` }}
                  />
                </div>
              </div>
            )}

            {/* 任务详情 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-4">
              <div>
                <span className="font-medium">{getLocalizedText('format')}</span>
                <span className="ml-1">{task.format || 'best'}</span>
              </div>
              
              <div>
                <span className="font-medium">{getLocalizedText('fileType')}</span>
                <span className="ml-1">{task.fileExt || 'mp4'}</span>
              </div>
              
              {task.filesize && (
                <div>
                  <span className="font-medium">{getLocalizedText('size')}</span>
                  <span className="ml-1">{formatFileSize(task.filesize)}</span>
                </div>
              )}
              
              <div>
                <span className="font-medium">{getLocalizedText('createdTime')}</span>
                <span className="ml-1">{formatCreatedAt(task.createdAt)}</span>
              </div>
            </div>

            {/* 操作按钮 */}
            {task.status === DOWNLOAD_STATUS.COMPLETED && (
              <div className="flex gap-3">
                <button
                  onClick={() => onDownloadFile(task.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <FileDown className="w-4 h-4" />
                  {getLocalizedText('downloadFile')}
                </button>
              </div>
            )}

            {/* 错误信息 */}
            {task.status === DOWNLOAD_STATUS.FAILED && task.error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">
                  <span className="font-medium">{getLocalizedText('error')}</span> {task.error}
                </p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DownloadProgress; 